"use client";

import { MinusIcon, XIcon } from "lucide-react";
import { Button } from "./button";

/** Windows 10-style maximize glyph: a 10px square with a 1px stroke. */
function MaximizeIcon() {
  return (
    <svg viewBox="0 0 10 10" className="size-2.5" fill="none" aria-hidden="true">
      <rect x="0.5" y="0.5" width="9" height="9" stroke="currentColor" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg viewBox="0 0 10 10" className="size-2.5" fill="none" aria-hidden="true">
      <path d="M2.5 2.5V0.5H9.5V7.5H7.5" stroke="currentColor" />
      <rect x="0.5" y="2.5" width="7" height="7" stroke="currentColor" />
    </svg>
  );
}

/** The three self-drawn window buttons a frameless desktop window puts in its title bar. */
export function WindowControls({
  maximized,
  minimizeLabel,
  maximizeLabel,
  restoreLabel,
  closeLabel,
  onMinimize,
  onToggleMaximize,
  onClose,
}: {
  maximized: boolean;
  minimizeLabel: string;
  maximizeLabel: string;
  restoreLabel: string;
  closeLabel: string;
  onMinimize: () => void;
  onToggleMaximize: () => void;
  onClose: () => void;
}) {
  return (
    <div
      data-slot="window-controls"
      data-testid="desktop-window-controls"
      className="ms-auto flex shrink-0 items-center gap-0.5 [app-region:no-drag]"
    >
      <Button
        data-testid="window-control-minimize"
        type="button"
        variant="ghost"
        size="icon-md"
        className="text-foreground hover:bg-hover"
        aria-label={minimizeLabel}
        onClick={onMinimize}
      >
        <MinusIcon className="size-4" />
      </Button>
      <Button
        data-testid="window-control-maximize"
        type="button"
        variant="ghost"
        size="icon-md"
        className="text-foreground hover:bg-hover"
        aria-label={maximized ? restoreLabel : maximizeLabel}
        onClick={onToggleMaximize}
      >
        {maximized ? <RestoreIcon /> : <MaximizeIcon />}
      </Button>
      <Button
        data-testid="window-control-close"
        type="button"
        variant="ghost"
        size="icon-md"
        className="text-foreground hover:bg-destructive hover:text-destructive-foreground"
        aria-label={closeLabel}
        onClick={onClose}
      >
        <XIcon className="size-4" />
      </Button>
    </div>
  );
}
